import {LitElement, css, html, nothing} from "lit";
import {Directive, directive} from "lit/directive.js";
import {repeat} from "lit/directives/repeat.js";
import {MIN_YEAR, MAX_YEAR, formatMoney} from "./profile-finance-grid.js";

export const LANG_DEF = {
	"en": {"activa": "Liabilities", "passiva": "Assets", "total.liabilities": "Total Liabilities", "total.assets": "Total Assets", "type.EBIT": "EBIT", "type.REVENUE": "Revenue"},
	"de": {"activa": "Aktiva", "passiva": "Passiva", "total.liabilities": "Verbindlichkeiten Gesamt", "total.assets": "Gesamtvermögen", "type.EBIT": "EBIT", "type.REVENUE": "Umsatz"},
};

class Lang extends Directive {

	render(code, key) {
		return html`${LANG_DEF[code][key] ?? key}`;
	}

}

export const lang = directive(Lang);

export class ProfileFinanceGridBalanceSheet extends LitElement {

	static get properties() {
		return {
			lang: { type: String, reflect: true },
			mode: { type: String, reflect: true },
			hasChanged: { type: Boolean },
			profileID: { type: String, attribute: "profile-id", reflect: true },
			loading: { type: Boolean, attribute: false },
			error: { type: String, attribute: false },
			year: { type: Number, attribute: false },
			columns: { type: Array, attribute: false },
			data: { type: Array, attribute: false }
		};
	}

	get hasChanged() {
		return Object.keys(this._changes).length > 0;
	}

	constructor() {
		super();

		this.lang = "en";

		this.mode = "read";

		/**
		 * @type {{}} {BalanceSheetID: {Year: Value} }
		 */
		this._initalData = {};

		/**
		 * Mutation Object
		 * @type {{}}
		 */
		this._changes = {};

		/**
		 * Map list of already loaded years.
		 *
		 * {Year: true}
		 */
		this._loadedYears = {};

		/**
		 * Next rows loading indicator. (Not for changing year view)
		 *
		 * @type {boolean}
		 */
		this.loading = false;

		/**
		 * The error message when something went wrong and the grid can no longer show valid data.
		 *
		 * @type {string|null}
		 */
		this.error = null;

		/**
		 * The end cursor of the cursor pagination.
		 *
		 * @type {string|null}
		 */
		this._endCursor = null;

		/**
		 * Indicates if further rows exists, and a loadNext is allowed.
		 *
		 * @type {boolean} Defaults to true for the initial load.
		 */
		this._hasNextPage = true;

		/**
		 * The initial year.
		 *
		 * The columns and rows render depending on this value.
		 *
		 * The columns are composed out of this value: (year - 2) & (year) & (year + 2) which are 5 years in total, with
		 * the current year in the center.
		 *
		 * @type {number}
		 */
		this.year = new Date().getFullYear();

		// Initial set of the columns
		this.updateColumns();

		/**
		 * The rows data.
		 * The year keys/properties of the row is the data for the column cell which is expected to be always present.
		 *
		 * [{ID: 1, Name: "Row Name", 2018: null, 2019: "2.00", 2020: null, ...}, ...]
		 */
		this.data = [];

		this._activaData = [];
		this._activaTotalData = {};
		this._passivaData = [];
		this._passivaTotalData = {};
	}

	attributeChangedCallback(name, old, value) {
		super.attributeChangedCallback(name, old, value);

		switch(name) {
			case "profile-id":
				if(old !== undefined)
					this.reset();
				if(value)
					requestAnimationFrame(() => this.loadNext());
				break;
		}
	}

	reset() {
		this._loadedYears = {};
		this.mode = "read";
		this._initalData = {};
		this._changes = {};
		this.year = new Date().getFullYear();
		this.updateColumns();
		this._endCursor = null;
		this._hasNextPage = true;
		this.error = null;
		this.loading = false;
		this.data = [];
		this._activaData = [];
		this._activaTotalData = {};
		this._passivaData = [];
		this._passivaTotalData = {};
	}

	/**
	 * Changes the view of the years by adding 1.
	 */
	nextYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to add 3 to check
		// the most right year not to exceed the limit.
		if(this.year+3 > MAX_YEAR)
			return;

		this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "next"
			}
		}));

		this.year++;
		this.updateColumns();
		this.load(this.columns[this.columns.length-1]);
	}

	/**
	 * Changes the view of the years by subtracting 1.
	 */
	prevYear() {
		// Because the table is always showing 5 years, and this.year is the middle one, we have to subtract 3 to check
		// the most left year not to exceed the limit.
		if(this.year-3 < MIN_YEAR)
			return;

		this.dispatchEvent(new CustomEvent("profile-finance-grid-year-change", {
			composed: true,
			detail: {
				target: this,
				direction: "prev"
			}
		}));

		this.year--;
		this.updateColumns();
		this.load(this.columns[0]);
	}

	/**
	 * Loads the next rows.
	 */
	loadNext() {
		if(this._hasNextPage)
			this.load(this.columns[0], this.columns[this.columns.length-1]);
	}

	/**
	 * Updates the viewed column years.
	 */
	updateColumns() {
		this.columns = [this.year - 2, this.year - 1, this.year, this.year + 1, this.year + 2];
	}

	hasChanges() {
		return this.hasChanged;
	}

	getChanges() {
		const changes = JSON.parse(JSON.stringify(this._changes));

		if(Object.keys(changes).length > 0) {
			// Convert Map {BalanceSheetID: Value} to array [{BalanceSheetID: BalanceSheetID, Year: Year, Value: Value}]
			const array = [];
			for(let balanceSheetID in changes) {
				for(let year in changes[balanceSheetID])
					array.push({ BalanceSheetID: balanceSheetID, Year: year, Value: changes[balanceSheetID][year] });
			}

			return {
				Finance: {
					BalanceSheetValues: array
				}
			}
		}

		return {};
	}

	resetChanges() {
		if(!this.hasChanges())
			return;

		// Go through all rows and check if they were changed
		for(let i in this.data) {
			let balanceSheet = this.data[i];

			// Has this Balance Sheet a change?
			if(!this._changes[balanceSheet.ID])
				continue;

			// Yes. Revert it.
			for(let year in this._changes[balanceSheet.ID]) {
				balanceSheet[year] = this._initalData[balanceSheet.ID][year];

				// Update Total
				this.updateActivaTotal(year);
				this.updatePassivaTotal(year);
			}
		}

		// Clear any recorded changes
		this._changes = {};

		// Update DOM
		this.requestUpdate();

		this._dispatchChangeEvent();
	}

	saveChanges() {
		if(!this.hasChanges())
			return;

		// Go through all changes and set them into the initial data.
		for(let balanceSheetID in this._changes) {
			for(let year in this._changes[balanceSheetID])
				this._initalData[balanceSheetID][year] = this._changes[balanceSheetID][year];
		}

		// Clear any recorded changes
		this._changes = {};

		this._dispatchChangeEvent();
	}

	_onValueChange(value, balanceSheet, year) {
		if(value === "")
			value = null;
		balanceSheet[year] = value;

		// Change detection
		const initValue = this._initalData[balanceSheet.ID][year];
		if(value !== initValue) {
			this.__setValueByPath(balanceSheet.ID + "." + year, value, this._changes);
		} else {
			this.__removeKeyByPath(balanceSheet.ID + "." + year, this._changes);
		}

		// Update Total
		if(balanceSheet.Type.startsWith("ACTIVA_")) this.updateActivaTotal(year);
		else if(balanceSheet.Type.startsWith("PASSIVA_")) this.updatePassivaTotal(year);

		// Update DOM
		this.requestUpdate();

		this._dispatchChangeEvent();
	}

	_dispatchChangeEvent() {
		this.dispatchEvent(new CustomEvent("profile-finance-grid-changed", {
			composed: true,
			detail: {
				value: this.hasChanged
			}
		}));
	}

	/**
	 * Loads the table data.
	 *
	 * The next rows will be loaded when fromYear and toYear is provided.
	 *
	 * Only the data for the column year is loaded when only fromYear is provided.
	 */
	load(fromYear, toYear = null) {
		this.error = null;

		if(!this.profileID) {
			this.error = "No Profile ID defined. " + this.profileID;
			return;
		}

		const query = `
			query ProfileBalanceSheetValues($profileID: ID!, $fromYear: Int!, $toYear: Int, $first: Int, $after: String) {
				listBalanceSheet(first: $first, after: $after) {
					edges {
						node {
							ID
							Name
							Type
							ProfileValues(profileID: $profileID, fromYear: $fromYear, toYear: $toYear, fillYears: true) {
								Year
								Value {Amount}
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`;

		const variables = {
			profileID: this.profileID,
			first: 10,
			fromYear: fromYear,
			toYear: toYear,
			fillYears: true
		};

		if(toYear !== null) {
			// Next loading
			this.loading = true;

			if(this._endCursor !== null) {
				variables.after = this._endCursor;

				// Reset loaded years, because if we load the next rows, we only have the loaded years of the
				// previous years and would not see the values of the new rows when changing the year view.
				this._loadedYears = {};
			}
		} else {
			// Skip, because year is already loaded.
			if(this._loadedYears.hasOwnProperty(fromYear))
				return;

			// Partial year update, load all years of previously loaded rows
			variables.first = this.data.length;
		}

		fetch("/graphql", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"Accept": "application/json"
			},
			body: JSON.stringify({
				query: query,
				variables: variables
			})
		}).then(r => r.json())
		.then(response => {
			if(!response || response.errors) {
				this.error = "Data Error:" + JSON.stringify(response);
				return;
			}

			const edges = response.data.listBalanceSheet.edges;
			if(edges.length === 0)
				return;

			if(toYear !== null) {
				this._endCursor = response.data.listBalanceSheet.pageInfo.endCursor;
				this._hasNextPage = response.data.listBalanceSheet.pageInfo.hasNextPage;

				// Next load of entries, add to table
				this.data.push(...edges.map(item => {
					const balanceSheet = item.node;

					// Turns years in ProfileValues object as keys in the balanceSheet variable.
					const years = {};
					balanceSheet.ProfileValues.forEach(balanceSheetValue => {
						years[balanceSheetValue.Year] = balanceSheetValue.Value !== null ? balanceSheetValue.Value.Amount : null;

						// Cache year as loaded
						this._loadedYears[balanceSheetValue.Year] = true;

						// Set initial data of loaded Balance Sheet, for change detection
						if(!this._initalData[balanceSheet.ID])
							this._initalData[balanceSheet.ID] = {};
						this._initalData[balanceSheet.ID][balanceSheetValue.Year] = years[balanceSheetValue.Year];
					});

					Object.assign(balanceSheet, years);

					delete balanceSheet.ProfileValues;

					return balanceSheet;
				}));
			} else {
				// Partial update, just update/insert entries of the year
				for(let i in this.data) {
					const balanceSheetEntry = this.data[i];

					for(let i2 in edges) {
						const balanceSheet = edges[i2].node;

						if(balanceSheet.ID === balanceSheetEntry.ID) {
							// Turns years in ProfileValues object as keys in the balanceSheet variable.
							balanceSheet.ProfileValues.forEach(balanceSheetValue => {
								this.data[i][balanceSheetValue.Year] = balanceSheetValue.Value !== null ? balanceSheetValue.Value.Amount : null;

								// Cache year as loaded
								this._loadedYears[balanceSheetValue.Year] = true;

								// Set initial data of loaded Balance Sheet, for change detection
								if(!this._initalData[balanceSheet.ID])
									this._initalData[balanceSheet.ID] = {};
								this._initalData[balanceSheet.ID][balanceSheetValue.Year] = this.data[i][balanceSheetValue.Year];
							});
							break;
						}
					}
				}
			}

			// Update Filtered data
			this._activaData = this.data.filter(item => item.Type.startsWith("ACTIVA_"));
			this.updateActivaTotal();
			this._passivaData = this.data.filter(item => item.Type.startsWith("PASSIVA_"));
			this.updatePassivaTotal();

			// Update DOM
			this.requestUpdate();
		})
		.catch(reason => this.error = reason)
		.finally(() => this.loading = false);
	}

	updateActivaTotal(yearToUpdate = null) {
		const updateYear = year => {
			let total = null;

			this._activaData.forEach(item => {
				if(Utils.isSet(item[year]))
					total += parseFloat(item[year]);
			});

			if(total !== null)
				this._activaTotalData[year] = total;
			else if(this._activaTotalData[year])
				delete this._activaTotalData[year];
		};

		if(yearToUpdate !== null)
			updateYear(yearToUpdate);
		else
			this.columns.forEach(year => updateYear(year));
	}

	updatePassivaTotal(yearToUpdate = null) {
		const updateYear = year => {
			let total = null;

			this._passivaData.forEach(item => {
				if(Utils.isSet(item[year]))
					total += parseFloat(item[year]);
			});

			if(total !== null)
				this._passivaTotalData[year] = total;
			else if(this._passivaTotalData[year])
				delete this._passivaTotalData[year];
		};

		if(yearToUpdate !== null)
			updateYear(yearToUpdate);
		else
			this.columns.forEach(year => updateYear(year));
	}

	static get styles() {
		return css`
			:host {
				display: block;
			}
			
			.container {
				padding-bottom: 16px;
			}
			
			.grid-table {
				display: grid;
				grid-template-columns: 2fr repeat(5, 1fr);
			}
			
			/* Affects all first column cells (the first and every 6th item) */
			.grid-table > :nth-child(6n+1) {
				text-align: left;
				border-left: none;
			}
			
			.grid-table > div {
				display: flex;
				flex-direction: column;
			    justify-content: center;
			    padding: 6px 24px;
			    border-bottom: 1px solid var(--divider-color);
			    border-left: 1px solid var(--divider-color);
			    text-align: center;
			}
			
			.column {
				position: relative;
				padding: 16px 24px;
				
				/* --paper-font-body2 */
				font-size: 14px;
		        font-weight: 500;
		        line-height: 24px;
			}
			
			.column button {
				position: absolute;
				background: transparent;
				height: calc(100% - 6px);
				margin: 0px 3px;
				padding: 0;
				border: 1px solid transparent;
				border-radius: 3px;
				color: var(--primary-color);
			}
			
			.column button:focus {
				border: 1px solid var(--primary-color);
				outline: none;
			}
			
			.column button:hover {
				background: var(--divider-color);
			}
			
			.column button:active {
				background: var(--primary-color);
				color: #FFF;
			}
			
			.column.prev button {
				left: 0;
			}
			
			.column.next button {
				right: 0;
			}
			
			.cell {			
				/* Hack */
				--paper-input-container_-_padding: 0px;
				
				/* --paper-font-body1 */
				font-size: 14px;
		        font-weight: 400;
		        line-height: 20px;
			}
			
			.cell.value {
				padding: 6px;
			}
			
			.cell.section {
				background: rgba(0, 0, 0, .02)
			}
			
			.cell.section.label {
				font-weight: bold;
			}
			
			.cell.total {
				border-top: 1px solid var(--divider-color);
			}
			
			.cell paper-spinner-lite {
				align-self: center;
				width: 16px;
				height: 16px;
				--paper-spinner-stroke-width: 2px;
			}
			
			.loading {
				display: flex;
				flex-direction: column;
				padding: 16px;
				border-bottom: 1px solid var(--divider-color);
			}
			
			.loading > paper-spinner-lite {
				align-self: center;
			}
			
			.loading > .error {
				color: var(--error-color);
			}
		`;
	}

	render() {
		return html`
            <div class="container">
                <section class="grid-table">
                    <!-- Columns -->
                    <div class="column">Name ${this.hasChanged ? "*" : nothing}</div>
                     ${this.columns.map((year, index) => {
                     	if(index === 0)
                     		return this._prevColumn(year);
                     	if(index === this.columns.length-1)
                     		return this._nextColumn(year);
                     	return html`<div class="column">${year}</div>`;
					})}

                    <!-- Rows Activa -->
                    ${!this.loading && this.error === null && this.data.length > 0 ? html`
                    <div class="cell section label">${lang(this.lang, "activa")}</div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    ` : nothing}
                    
	                ${repeat(this._activaData, balanceSheet => balanceSheet.ID, balanceSheet => html`
						<div class="cell">${balanceSheet.Name}</div>
						${this.columns.map(year => this._cell(balanceSheet, year))}
					`)}
	                
	                <!-- Activa Total -->
	                ${!this.loading && this.error === null && this.data.length > 0 ? html`
					<div class="cell total">${lang(this.lang, "total.liabilities")}</div>
					${this.columns.map(year => html`<div class="cell total">${this._loadedYears[year] ? html`${formatMoney(this._activaTotalData[year])}` : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>`)}` : nothing}
	                
	                <!-- Rows Passiva -->
	                ${!this.loading && this.error === null && this.data.length > 0 ? html`
	                <div class="cell section label">${lang(this.lang, "passiva")}</div>
	                <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
                    <div class="cell section"></div>
	                ` : nothing}
                    
	                 ${repeat(this._passivaData, balanceSheet => balanceSheet.ID, balanceSheet => html`
						<div class="cell">${balanceSheet.Name}</div>
						${this.columns.map(year => this._cell(balanceSheet, year))}
					`)}
	                 
	                 <!-- Passiva Total -->
					${!this.loading && this.error === null && this.data.length > 0 ? html`
					<div class="cell total">${lang(this.lang, "total.assets")}</div>
					${this.columns.map(year => html`<div class="cell total">${this._loadedYears[year] ? html`${formatMoney(this._passivaTotalData[year])}` : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>`)}` : nothing}
                </section>
                
                ${(this.loading || this.error !== null) ? html`
                    <div class="loading">
						${this.loading ? html`<paper-spinner-lite active></paper-spinner-lite>` : nothing}
						${this.error !== null ? html`<div class="error">Error: ${this.error}</div>` : nothing}
					</div>
                ` : nothing}
            </div>
		`;
	}

	_prevColumn(year) {
		return html`
			<div class="column prev">
				${this.data.length > 0 && this.year-3 >= MIN_YEAR ? html`
				<button @click="${this.prevYear}">
					<paper-ripple></paper-ripple>
					<iron-icon icon="chevron-left"></iron-icon>
				</button>
				` : nothing}
				
				<div>${year}</div>
			</div>
		`;
	}

	_nextColumn(year) {
		return html`
			<div class="column next">
				${this.data.length > 0 && this.year+3 <= MAX_YEAR ? html`
				<button @click="${this.nextYear}">
					<paper-ripple></paper-ripple>
					<iron-icon icon="chevron-right"></iron-icon>
				</button>
				` : nothing}
				
				<div>${year}</div>
			</div>
		`;
	}

	_cell(balanceSheet, year) {
		return html`<div class="cell value">${this._loadedYears[year] ? (this.mode === "write" ? this._cellWrite(balanceSheet, year) : this._cellRead(balanceSheet, year)) : html`<paper-spinner-lite active></paper-spinner-lite>`}</div>`;
	}

	_cellRead(balanceSheet, year) {
		return html`${formatMoney(balanceSheet[year])}`;
	}

	_cellWrite(balanceSheet, year) {
		return html`<paper-input no-label-float placeholder="-" allowed-pattern="[0-9.]" auto-validate .value=${balanceSheet[year]} @change=${e => this._onValueChange(e.target.value, balanceSheet, year)}></paper-input>`;
	}

	/**
	 * Will set the value on a specific path, sub-properties will be created if not exists.
	 *
	 * E.g.:
	 * path = user.metadata.location
	 * value = "Berlin"
	 * searchObject = {}
	 * turns into:
	 * {
	 *      user: {
	 *          metadata: {
	 *              location: "Berlin"
	 *          }
	 *      }
	 * }
	 *
	 * @param {string} path
	 * @param value
	 * @param {Object} searchObject
	 * @private
	 */
	__setValueByPath(path, value, searchObject) {
		const pathProps = path.split(".");
		for(let i = 0, len = pathProps.length, prop = searchObject; i < len; i++) {
			const propName = pathProps[i];
			if((i+1) < len) {
				prop = prop.hasOwnProperty(propName) ? prop[propName] : (prop[propName] = {});
			} else {
				// Reached end of path. Set value
				return prop[propName] = (value);
			}
		}
	}

	/**
	 * Will removes the end key of the path and any empty key
	 *
	 * E.g.1:
	 * path = user.metadata.location
	 * object = {
	 *      user: {
	 *          metadata: {
	 *              location: "Berlin"
	 *          }
	 *      }
	 * }
	 * turns into:
	 * object = {}
	 *
	 * E.g.2:
	 * path = user.metadata.location
	 * object = {
	 *      user: {
	 *          metadata: {
	 *              location: "Berlin",
	 *              timezone: "Europe/Berlin"
	 *          }
	 *      }
	 * }
	 * turns into:
	 * object = {
	 *      user: {
	 *          metadata: {
	 *              timezone: "Europe/Berlin"
	 *          }
	 *      }
	 * }
	 *
	 * @param {string} path
	 * @param {Object} object
	 * @return {Boolean} Returns true if the removing of the path was successful. false otherwise.
	 * @private
	 */
	__removeKeyByPath(path, object) {
		const keys = path.split(".");

		let previous = [], current = object;
		for(let i = 0, len = keys.length; i < len; i++) {
			if(current.hasOwnProperty(keys[i])) {
				const isLast = i === keys.length-1;
				if(isLast) {
					while(i >= 0) {
						// Continue deleting previous as long object is empty
						delete current[keys[i]];
						if(Object.keys(current).length === 0) {
							i--;
							current = previous[i];
						} else
							break; // No further empty objects
					}

					return true;
				} else {
					previous.push(current);
					current = current[keys[i]];
				}
			} else
				return false;
		}

		return false;
	}

}

customElements.define("profile-finance-grid-balance-sheet", ProfileFinanceGridBalanceSheet);